'use client';

import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { BonusMinDepV2 } from '@/api/cms/bonuses';
import { DEFAULT_CURRENCIES } from '@/helpers/currency';
import { QUERY_VIP } from '@/helpers/modals';
import { useCMSCashierBonuses } from '@/modules/bonuses';
import { sendDepositBonusClickEvent } from '@/modules/googleAnalytics';
import { useDepositStore, useGetCommonStore } from '@/store';

import { useABIncreasePrevDep } from './useABIncreasePrevDep';

interface IUseToggleBonusCodeProps {
  id: number;
  code: string | undefined;
  isBonusActivated: boolean;
  onBeforeToggle?: () => void;
  onAfterSelectBonus?: () => void;
  bonusMinDeps?: BonusMinDepV2[];
}

export const useToggleBonusCode = ({
  id,
  code,
  isBonusActivated,
  onBeforeToggle,
  onAfterSelectBonus,
  bonusMinDeps,
}: IUseToggleBonusCodeProps) => {
  const useCommonStore = useGetCommonStore();
  const user = useCommonStore.use.user();
  const updateUser = useCommonStore.use.updateUser();
  const refreshUser = useCommonStore.use.refreshUser();

  const { bonuses: cashierBonuses } = useCMSCashierBonuses();
  const { increasedPrevDepValue } = useABIncreasePrevDep();
  const searchParams = useSearchParams();

  const { mode, handleSetBonusCode, handleRemoveBonusCode } = useDepositStore(
    useShallow((state) => ({
      mode: state.mode,
      handleSetBonusCode: state.handleSetBonusCode,
      handleRemoveBonusCode: state.handleRemoveBonusCode,
    }))
  );

  const selectBonus = useCallback(async () => {
    if (!(user && code)) return;

    await handleSetBonusCode({
      user,
      updateUser,
      refreshUser,
      values: { depositBonusCode: code },
      bonuses: cashierBonuses,
      bonusMinDeps,
      increasedPrevDepValue,
      currencies: DEFAULT_CURRENCIES,
      isVIP: !!searchParams.get(QUERY_VIP),
    });

    onAfterSelectBonus?.();
  }, [
    user,
    code,
    cashierBonuses,
    bonusMinDeps,
    increasedPrevDepValue,
    updateUser,
    handleSetBonusCode,
    onAfterSelectBonus,
    searchParams,
    refreshUser,
  ]);

  const handleToggleBonus = useCallback(async () => {
    onBeforeToggle?.();

    sendDepositBonusClickEvent({
      dep_modal_type: mode,
      type: isBonusActivated ? 'off' : 'on',
      id,
    });

    if (isBonusActivated) {
      await handleRemoveBonusCode({ user, updateUser, refreshUser });
    } else {
      await selectBonus();
    }
  }, [
    onBeforeToggle,
    isBonusActivated,
    id,
    mode,
    user,
    updateUser,
    handleRemoveBonusCode,
    selectBonus,
    refreshUser,
  ]);

  return { handleToggleBonus };
};
